import {useState, KeyboardEvent} from "react";
import {Input} from "@/components/ui/input.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import {PlusIcon, XMarkIcon} from "@heroicons/react/24/outline";

type TagListInputProps = {
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: string;
};

/**
 * שדה קלט המאפשר הוספה/הסרה נוחה של כמה ערכים (לדוגמה: כמה תשובות נכונות לאותה שאלה).
 * הוספה: הקלדה + Enter או לחיצה על כפתור ה-"+". הסרה: לחיצה על ה-X שבתוך כל תגית.
 */
export function TagListInput({value, onChange, placeholder}: TagListInputProps) {
    const [draft, setDraft] = useState('');

    function addTag() {
        const trimmed = draft.trim();
        if (!trimmed) return;
        if (!value.includes(trimmed)) {
            onChange([...value, trimmed]);
        }
        setDraft('');
    }

    function removeTag(tag: string) {
        onChange(value.filter(v => v !== tag));
    }

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
        } else if (e.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1));
        }
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder || 'הקלד ערך ולחץ Enter להוספה'}
                    className="flex-grow"
                />
                <Button type="button" size="icon" variant="outline" onClick={addTag} className="shrink-0">
                    <PlusIcon className="size-4"/>
                </Button>
            </div>

            {value.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                    {value.map((tag, index) => (
                        <Badge key={`${tag}-${index}`} variant="secondary" className="gap-1 py-1 px-2">
                            <span>{tag}</span>
                            <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="cursor-pointer rounded-full hover:bg-neutral-300/60 p-0.5"
                            >
                                <XMarkIcon className="size-3"/>
                            </button>
                        </Badge>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-neutral-400">לא הוגדרו ערכים עדיין</p>
            )}
        </div>
    );
}

